import { currencyFormatter } from '@app/utils/pricingCurrencyUtils';
import type { BaseColumn } from '@konnecta/generic-table';
import { Button, Typography } from 'antd';
import { TFunction } from 'i18next';
import type { CaregiverCashRow } from '../../types';
import { createdAtColumn } from '@app/components/common/createdAtColumn';

interface CaregiverCashTableHandlers {
  onRecordDeposit: (row: CaregiverCashRow) => void;
}

export const getCaregiverCashTableColumns = (
  t: TFunction,
  locale: string,
  handlers?: CaregiverCashTableHandlers
): BaseColumn<CaregiverCashRow>[] => [
  {
    id: 'name',
    header: t('caregiver-cash-col-name'),
    accessor: 'name',
    width: 260,
    sortable: true,
    sortField: 'name',
    copy: false,
    cellRenderer: (value) => <Typography>{(value as string) || '-'}</Typography>,
  },
  {
    id: 'balance',
    header: t('caregiver-cash-col-cash-on-hand'),
    accessor: 'balance',
    align: 'right',
    width: 350,
    // Pin right (like actions) so the lib does NOT pick this as the "fill"
    // column. The lib stretches the last non-sticky column to fill the table
    // width; marking balance sticky makes `name` the fill column instead, so
    // the caregiver name absorbs the slack and cash-on-hand stays a fixed 200px.
    sticky: 'right',
    sortable: true,
    sortField: 'balance',
    copy: false,
    cellRenderer: (value, row) => (
      <div className="flex w-full justify-end">
        <Typography className="tabular-nums" style={{ fontWeight: 600 }}>
          {currencyFormatter(value as number, row.currency, locale)}
        </Typography>
      </div>
    ),
  },
  // Pinned for the same reason as balance: `name` stays the fill column.
  { ...createdAtColumn<CaregiverCashRow>(t), sticky: 'right' },
  {
    id: 'actions',
    header: t('caregiver-cash-col-actions'),
    accessor: 'id',
    align: 'center',
    sticky: 'right',
    width: 230,
    sortable: false,
    copy: false,
    filterable: false,
    cellRenderer: (_value, row) => (
      <Button type="link" size="small" onClick={() => handlers?.onRecordDeposit(row)}>
        {t('caregiver-cash-record-deposit')}
      </Button>
    ),
  },
];
